import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Box, CircularProgress, Dialog, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { colors } from '../../const/colors'
import { dateIso2localeString } from '../../utils/formatDate'
import { DayEntry } from '../../utils/reservationDays'
import { useSnackbar } from '../Shared/useSnackbar'
import DayCheckbox from './DayCheckbox'
import {
  CANCEL_PARKING_SUBSCRIPTION_FOR_USER,
  ICancelParkingSubscriptionForUserData,
  ICancelParkingSubscriptionForUserVars,
} from '../../graphql/parkingPool/cancelParkingSubscriptionForUser'
import {
  CANCEL_PARKING_POOL_RESERVATION_DAY,
  ICancelParkingPoolReservationDayData,
  ICancelParkingPoolReservationDayVars,
} from '../../graphql/parkingPool/cancelParkingPoolReservationDay'

type CancelScope = 'whole' | 'days'

interface CancelReservationModalProps {
  open: boolean
  onClose: () => void
  login: string
  subscriptionId: string
  fullName: string
  parking: string
  periodLabel: string
  days: DayEntry[]
  daysLoading: boolean
  onCancelled: () => void
}

const ScopeRadio = ({ active }: { active: boolean }) => (
  <Box
    sx={{
      flexShrink: 0,
      width: 19,
      height: 19,
      borderRadius: '50%',
      border: `1.5px solid ${active ? colors.navy : colors.border3}`,
      background: colors.surface,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'border-color .12s',
    }}
  >
    {active && (
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: colors.navy,
        }}
      />
    )}
  </Box>
)

const OPTION_SX = (active: boolean) => ({
  flex: 1,
  display: 'flex',
  gap: '10px',
  p: '13px 14px',
  borderRadius: '14px',
  border: `1.5px solid ${active ? colors.navy : colors.border3}`,
  background: active ? colors.rowSelected : colors.surface,
  cursor: 'pointer',
  userSelect: 'none' as const,
  transition: 'border-color .12s, background .12s',
  '&:hover': { borderColor: active ? colors.navy : colors.slateLight },
})

const CancelReservationModal = ({
  open,
  onClose,
  login,
  subscriptionId,
  fullName,
  parking,
  periodLabel,
  days,
  daysLoading,
  onCancelled,
}: CancelReservationModalProps) => {
  const { t, i18n } = useTranslation(['parking_pool_reservations', 'generic'])
  const { notify, notifyError } = useSnackbar()
  const [scope, setScope] = useState<CancelScope>('whole')
  const [selectedDays, setSelectedDays] = useState<string[]>([])
  const [notifyUser, setNotifyUser] = useState(true)

  useEffect(() => {
    if (open) {
      setScope('whole')
      setSelectedDays([])
      setNotifyUser(true)
    }
  }, [open, subscriptionId])

  const [cancelReservation, { loading: cancellingWhole }] = useMutation<
    ICancelParkingSubscriptionForUserData,
    ICancelParkingSubscriptionForUserVars
  >(CANCEL_PARKING_SUBSCRIPTION_FOR_USER, { onError: notifyError })

  const [cancelDays, { loading: cancellingDays }] = useMutation<
    ICancelParkingPoolReservationDayData,
    ICancelParkingPoolReservationDayVars
  >(CANCEL_PARKING_POOL_RESERVATION_DAY, { onError: notifyError })

  const cancelling = cancellingWhole || cancellingDays
  const dayCancelAvailable = !daysLoading && days.length > 0
  const allDaysSelected =
    days.length > 0 && selectedDays.length === days.length

  const dayLabel = useMemo(
    () => (iso: string) =>
      dateIso2localeString(iso, i18n.language, {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language],
  )

  const toggleDay = (day: string) =>
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(item => item !== day) : [...prev, day],
    )

  const confirm = async () => {
    if (cancelling) return

    // TO DO!!! poprosić backend o argument notify w cancelParkingPoolReservationDay —
    // przy anulowaniu dni użytkownik nie dostaje żadnego powiadomienia
    if (scope === 'days') {
      if (!selectedDays.length) return
      const res = await cancelDays({
        variables: {
          parkingSubscription: subscriptionId,
          days: selectedDays,
          comment: t('cancel_default_comment'),
        },
      })
      const payload = res.data?.cancelParkingPoolReservationDay
      if (payload?.status) {
        onClose()
        notify(t('toast_days_cancelled', { n: selectedDays.length }))
        onCancelled()
      } else if (payload?.message) {
        notifyError(payload.message)
      }
      return
    }

    const res = await cancelReservation({
      variables: {
        login,
        parkingSubscription: subscriptionId,
        comment: t('cancel_default_comment'),
        notify: notifyUser,
      },
    })
    const payload = res.data?.cancelParkingSubscriptionForUser
    if (payload?.status) {
      onClose()
      notify(t('toast_cancelled_one', { parking }))
      onCancelled()
    } else if (payload?.message) {
      notifyError(payload.message)
    }
  }

  const confirmDisabled =
    cancelling || (scope === 'days' && selectedDays.length === 0)

  return (
    <Dialog
      open={open}
      onClose={cancelling ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: colors.radiusModal,
          width: '520px',
          maxWidth: '100%',
          p: '28px',
          boxShadow: '0 30px 80px -20px rgba(19,54,73,.35)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: '6px',
        }}
      >
        <Typography sx={{ fontSize: '20px', fontWeight: 800 }}>
          {t('cancel_reservation')}
        </Typography>
        <Box
          onClick={cancelling ? undefined : onClose}
          sx={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: '#EAF1F4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: cancelling ? 'default' : 'pointer',
            color: colors.textSecondary,
            fontSize: '18px',
            lineHeight: 1,
            flexShrink: 0,
            userSelect: 'none',
            opacity: cancelling ? 0.5 : 1,
            '&:hover': { background: '#E1E9EE' },
          }}
        >
          ×
        </Box>
      </Box>

      <Typography
        sx={{ fontSize: '13.5px', color: colors.textMuted, mb: '18px' }}
      >
        {t('reservation_of_user')}{' '}
        <Box component="strong" sx={{ color: colors.textBody }}>
          {fullName}
        </Box>
      </Typography>

      <Box
        sx={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: '14px',
          p: '16px',
          mb: '18px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {[
          { label: t('col_parking'), value: parking },
          { label: t('period'), value: periodLabel },
          {
            label: t('col_status'),
            value: t('status_active'),
            valueColor: colors.green,
          },
        ].map(({ label, value, valueColor }) => (
          <Box
            key={label}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '13.5px',
            }}
          >
            <Box component="span" sx={{ color: colors.textMuted }}>
              {label}
            </Box>
            <Box
              component="span"
              sx={{ fontWeight: 700, color: valueColor ?? colors.textBody }}
            >
              {value}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: '10px', mb: '16px' }}>
        {(
          [
            { value: 'whole', title: t('scope_whole'), desc: t('scope_whole_desc'), enabled: true },
            {
              value: 'days',
              title: t('scope_days'),
              desc: dayCancelAvailable
                ? t('scope_days_desc')
                : daysLoading
                  ? t('days_loading')
                  : t('days_unavailable'),
              enabled: dayCancelAvailable,
            },
          ] as const
        ).map(option => (
          <Box
            key={option.value}
            onClick={() => option.enabled && setScope(option.value)}
            sx={{
              ...OPTION_SX(scope === option.value && option.enabled),
              opacity: option.enabled ? 1 : 0.6,
              cursor: option.enabled ? 'pointer' : 'default',
            }}
          >
            <ScopeRadio active={scope === option.value && option.enabled} />
            <Box>
              <Box
                sx={{ fontSize: '13.5px', fontWeight: 800, color: colors.navy }}
              >
                {option.title}
              </Box>
              <Box
                sx={{
                  fontSize: '12px',
                  color: colors.textMuted,
                  fontWeight: 600,
                  mt: '2px',
                  lineHeight: 1.35,
                }}
              >
                {option.desc}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {scope === 'days' && dayCancelAvailable && (
        <Box sx={{ mb: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: '8px',
            }}
          >
            <Box
              sx={{
                fontSize: '12px',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: colors.slate,
                fontWeight: 700,
              }}
            >
              {t('days_title', { n: days.length })}
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', fontSize: '12.5px', fontWeight: 700 }}>
              <Box
                onClick={() => setSelectedDays(days.map(entry => entry.day))}
                sx={{ color: colors.navy, cursor: 'pointer', userSelect: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {t('days_select_all')}
              </Box>
              <Box
                onClick={() => setSelectedDays([])}
                sx={{ color: colors.slate, cursor: 'pointer', userSelect: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {t('days_clear')}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              border: `1px solid ${colors.border}`,
              borderRadius: '14px',
              maxHeight: '224px',
              overflowY: 'auto',
            }}
          >
            {days.map((entry, index) => {
              const checked = selectedDays.includes(entry.day)
              return (
                <Box
                  key={entry.day}
                  onClick={() => toggleDay(entry.day)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    p: '10px 14px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderTop: index === 0 ? 'none' : `1px solid ${colors.border2}`,
                    background: checked ? colors.rowSelected : 'transparent',
                    '&:hover': { background: checked ? colors.rowSelected : colors.rowHover },
                  }}
                >
                  <DayCheckbox checked={checked} />
                  <Box sx={{ fontSize: '13.5px', fontWeight: 700, color: colors.textBody }}>
                    {dayLabel(entry.day)}
                  </Box>
                  {entry.places.length > 0 && (
                    <Box
                      sx={{
                        fontSize: '12px',
                        color: colors.textMuted,
                        fontWeight: 600,
                        ml: 'auto',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.places.join(', ')}
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>

          {allDaysSelected && (
            <Box
              sx={{
                mt: '8px',
                fontSize: '12.5px',
                color: '#8a6a00',
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {t('days_all_selected_hint')}
            </Box>
          )}
        </Box>
      )}

      {scope === 'whole' && (
        <Box
          onClick={() => setNotifyUser(prev => !prev)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: '#EAF1F4',
            borderRadius: '13px',
            p: '13px 16px',
            mb: '22px',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <DayCheckbox checked={notifyUser} />
          <Typography
            sx={{ fontSize: '13px', color: colors.textAccent, fontWeight: 600 }}
          >
            {t('notify_user')}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: '10px' }}>
        <Box
          onClick={cancelling ? undefined : onClose}
          sx={{
            flex: 1,
            height: '48px',
            borderRadius: colors.radiusBtn,
            border: `1px solid ${colors.border3}`,
            color: colors.textSecondary,
            fontWeight: 700,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: cancelling ? 'default' : 'pointer',
            userSelect: 'none',
            transition: 'border-color .12s',
            '&:hover': { borderColor: colors.slateLight },
          }}
        >
          {t('back')}
        </Box>
        <Box
          onClick={confirmDisabled ? undefined : confirm}
          data-e2e="confirm-cancel-reservation"
          sx={{
            flex: 1.6,
            height: '48px',
            borderRadius: colors.radiusBtn,
            background: confirmDisabled ? colors.redBorder : colors.red,
            color: '#fff',
            fontWeight: 700,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: confirmDisabled ? 'default' : 'pointer',
            userSelect: 'none',
            boxShadow: confirmDisabled ? 'none' : colors.shadowBtnRed,
            transition: 'background .12s',
            '&:hover': {
              background: confirmDisabled ? colors.redBorder : colors.redDark,
            },
          }}
        >
          {cancelling && (
            <CircularProgress size={15} sx={{ color: '#fff' }} thickness={5} />
          )}
          {scope === 'days'
            ? t('confirm_days', { n: selectedDays.length })
            : t('confirm_whole')}
        </Box>
      </Box>
    </Dialog>
  )
}

export default CancelReservationModal
